// Guida all'installazione della PWA (aggiunta alla schermata Home).
//
// Tre parti:
// - rilevamento: piattaforma/browser e stato "già installata";
// - preferenze del dipendente in localStorage (per dispositivo, come l'installazione);
// - piccolo store con l'evento beforeinstallprompt (solo Chromium) e lo stato
//   di apertura della guida, letto da use-install-guide.

import { isNativeApp } from './native-bridge';

const STORAGE_KEY = 'heyjob.installGuide';
const SNOOZE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

// Browser integrati nelle app (link aperti da WhatsApp, Gmail, Outlook…):
// da qui non si può aggiungere alla schermata Home.
const IN_APP_UA = /FBAN|FBAV|Instagram|LinkedInApp|Line\/|MicroMessenger|WhatsApp|Telegram|Outlook|GSA\/|Twitter/i;

// ----------------------------------------------------------------------
// Rilevamento

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return !!window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

/**
 * 'ios-safari' | 'ios-other' | 'android-chrome' | 'android-other' | 'in-app' | 'desktop'
 */
export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  // iPadOS si presenta come Mac: lo riconosce il touch
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(ua);

  if (!ios && !android) return 'desktop';
  if (IN_APP_UA.test(ua)) return 'in-app';

  if (ios) {
    // Le WebView iOS non hanno il token "Safari/" (Chrome/Edge/Firefox iOS sì)
    if (!/Safari\//.test(ua)) return 'in-app';
    return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? 'ios-other' : 'ios-safari';
  }

  if (/; wv\)/.test(ua)) return 'in-app';
  if (/Chrome\//.test(ua) && !/SamsungBrowser|EdgA|OPR|Firefox|YaBrowser|MiuiBrowser|HuaweiBrowser/.test(ua)) {
    return 'android-chrome';
  }
  return 'android-other';
}

/** true se ha senso proporre l'installazione su questo dispositivo. */
export function canOfferInstall() {
  return !isNativeApp() && !isStandalone() && detectPlatform() !== 'desktop';
}

// ----------------------------------------------------------------------
// Preferenze (localStorage può mancare o lanciare: si degrada a "mostra")

function readPrefs() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

function writePrefs(patch) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readPrefs(), ...patch }));
  } catch (error) {
    // storage non disponibile: la guida ricomparirà, nessun danno
  }
}

export function snoozeInstallGuide() {
  writePrefs({ snoozedUntil: Date.now() + SNOOZE_DAYS * DAY_MS });
}

export function dismissInstallGuideForever() {
  writePrefs({ dismissedForever: true });
}

export function shouldAutoShowInstallGuide() {
  if (!canOfferInstall()) return false;
  const prefs = readPrefs();
  if (prefs.dismissedForever) return false;
  return !prefs.snoozedUntil || Date.now() >= prefs.snoozedUntil;
}

// ----------------------------------------------------------------------
// Store: prompt di installazione + apertura guida

let deferredPrompt = null;
let snapshot = { promptAvailable: false, open: null }; // open: null | 'auto' | 'manual'
const listeners = new Set();

function update(patch) {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((listener) => listener());
}

export function subscribeInstallGuide(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getInstallGuideSnapshot() {
  return snapshot;
}

/**
 * Da chiamare all'avvio (main.jsx): Chrome invia beforeinstallprompt una sola
 * volta e può farlo prima che la dashboard sia montata.
 */
export function captureInstallPrompt() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    // Niente mini-infobar di Chrome: l'installazione la propone la guida
    event.preventDefault();
    deferredPrompt = event;
    update({ promptAvailable: true });
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    update({ promptAvailable: false });
  });
}

/** Mostra il dialogo nativo di Chrome. Ritorna 'accepted' | 'dismissed' | 'unavailable'. */
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const event = deferredPrompt;
  // L'evento si usa una volta sola
  deferredPrompt = null;
  update({ promptAvailable: false });
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}

export function openInstallGuide(mode = 'manual') {
  update({ open: mode });
}

export function closeInstallGuide() {
  update({ open: null });
}
